import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { OfflineBanner } from "../components/offline-banner";
import { ErrorPage } from "../components/pages/error-page";
import { NotFoundPage } from "../components/pages/not-found-page";
import { Toaster } from "../components/ui/toast";
import { isTypingTarget } from "../lib/hooks";
import { setShortcutsOpen, useUiState } from "../lib/ui-store";

const ShortcutsDialog = lazy(() => import("../components/shortcuts-dialog").then((m) => ({ default: m.ShortcutsDialog })));

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});

function RootLayout() {
  const { shortcutsOpen } = useUiState();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <OfflineBanner />
      <Outlet />
      <Toaster />
      {shortcutsOpen && (
        <Suspense fallback={null}>
          <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
        </Suspense>
      )}
    </>
  );
}
